/**
 * Ministry Team Service
 * Handles ministry team management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MinistryTeamService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MinistryTeamService.DEFAULT_SERVICE_PATH,
            basePath: MinistryTeamService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all ministry teams with optional filters
     * @param isActive - Optional filter for active/inactive teams
     * @param ministryId - Optional filter by ministry ID
     * @returns Array of ministry teams
     */
    async getAll(isActive, ministryId) {
        const response = await this.getAllRaw(isActive, ministryId);
        return response.data ?? [];
    }
    /**
     * Get all ministry teams with optional filters (raw response)
     * @param isActive - Optional filter for active/inactive teams
     * @param ministryId - Optional filter by ministry ID
     * @returns Raw response with array of ministry teams
     */
    getAllRaw(isActive, ministryId) {
        const params = {};
        if (isActive !== undefined)
            params.isActive = isActive;
        if (ministryId !== undefined)
            params.ministryId = ministryId;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get ministry team by ID
     * @param id - Ministry team ID
     * @returns Ministry team details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ministry team with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry team by ID (raw response)
     * @param id - Ministry team ID
     * @returns Raw response with ministry team details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new ministry team
     * @param data - Ministry team creation data
     * @returns Created ministry team
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create ministry team');
        }
        return response.data;
    }
    /**
     * Create a new ministry team (raw response)
     * @param data - Ministry team creation data
     * @returns Raw response with created ministry team
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing ministry team
     * @param id - Ministry team ID
     * @param data - Ministry team update data
     * @returns Updated ministry team
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update ministry team with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing ministry team (raw response)
     * @param id - Ministry team ID
     * @param data - Ministry team update data
     * @returns Raw response with updated ministry team
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a ministry team
     * @param id - Ministry team ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a ministry team (raw response)
     * @param id - Ministry team ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate ministry team cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate ministry team cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MinistryTeamService.DEFAULT_SERVICE_PATH = 'departments';
MinistryTeamService.BASE_PATH = 'api/ministryteams';
//# sourceMappingURL=MinistryTeamService.js.map